// packages/core/src/learning/SchoolMeta.ts
// ─────────────────────────────────────────────────────────────────────────
// Phase 2: the soul. Each school now carries a thinker (optional — skills
// have none), a one-line philosophy, an accent color, and 2–4 core beliefs.
//
// Colors are grouped by bucket family so the Schools index and the school
// landings feel like one museum:
//   core-money-skills            → greens
//   money-lenses                 → purples
//   investing (strategy)         → blues   / (speculation) → warm
//   freedom-lifestyle-paths      → teals
//   income-life-domains          → oranges
//   macro-wealth-perspectives    → slate
//
// `personal-finance-basics` is intentionally NOT listed (catch-all tag).
// Core beliefs are plain-language descriptions of each school's widely-held
// tenets — they are paraphrases of the philosophy, not quotations.
//
// `ListedSchool` lives in LearningTypes (single source of truth) so this
// file and SchoolExhibitMeta can both import it without collisions.
// ─────────────────────────────────────────────────────────────────────────

import type { ListedSchool } from './LearningTypes';

// ── Buckets (locked) ──────────────────────────────────────────────────────
export const SCHOOL_BUCKETS = [
	'core-money-skills',
	'money-lenses',
	'investing-strategies-speculation',
	'freedom-lifestyle-paths',
	'income-life-domains',
	'macro-wealth-perspectives',
] as const;

export type SchoolBucket = (typeof SCHOOL_BUCKETS)[number];

export interface BucketMeta {
	id: SchoolBucket;
	label: string;
	tagline?: string;
	order: number;
}

export const BUCKET_META: Record<SchoolBucket, BucketMeta> = {
	'core-money-skills': {
		id: 'core-money-skills',
		label: 'Core money skills',
		tagline: 'do this',
		order: 1,
	},
	'money-lenses': {
		id: 'money-lenses',
		label: 'Money lenses',
		tagline: 'think this way',
		order: 2,
	},
	'investing-strategies-speculation': {
		id: 'investing-strategies-speculation',
		label: 'Investing strategies & speculation',
		order: 3,
	},
	'freedom-lifestyle-paths': {
		id: 'freedom-lifestyle-paths',
		label: 'Freedom & lifestyle paths',
		order: 4,
	},
	'income-life-domains': {
		id: 'income-life-domains',
		label: 'Income & life domains',
		order: 5,
	},
	'macro-wealth-perspectives': {
		id: 'macro-wealth-perspectives',
		label: 'Macro & wealth perspectives',
		order: 6,
	},
};

// ── Schools ────────────────────────────────────────────────────────────────
export type SchoolGroup = 'strategy' | 'speculation';

export interface SchoolMetaEntry {
	id: ListedSchool;
	label: string;
	bucket: SchoolBucket;
	group?: SchoolGroup;
	/** The person most associated with the school. Omitted for skills/domains
	 *  with no single figurehead. */
	thinker?: string;
	/** One punchy sentence capturing the worldview. */
	philosophyLine: string;
	/** Arena-grade slogan (≤6 words ideally) for the Learn hub hero,
	 *  featured-worldview card, and carousel. Shorter & more provocative
	 *  than philosophyLine. Optional — only marquee schools carry it for now. */
	marketingHook?: string;
	/** Accent hex, used as --cat-color across index + landing. */
	color: string;
	/** 2–4 plain-language tenets (paraphrase, not quotes). */
	coreBeliefs: string[];
}

export const SCHOOL_META: Record<ListedSchool, SchoolMetaEntry> = {
	// ── 1 · Core money skills (greens) ──────────────────────────────────
	'credit-optimization': {
		id: 'credit-optimization',
		label: 'Credit optimization',
		bucket: 'core-money-skills',
		color: '#5b9d2f',
		philosophyLine:
			'Your credit score is a tool you leverage, not a grade you receive.',
		coreBeliefs: [
			'Pay on time, every time. Credit is a trust record and paymeny history is the loudest signal',
			'Keep utilization low and old accounts open.',
			'Credit is for leverage and rewards, not for spending money you don’t have.',
		],
	},
	'tax-optimization': {
		id: 'tax-optimization',
		label: 'Tax optimization',
		bucket: 'core-money-skills',
		color: '#4f8f3a',
		philosophyLine:
			'Legally keeping more of what you earn beats earning more and losing it.',
		coreBeliefs: [
			'Use tax-advantaged accounts before taxable ones.',
			'Understand marginal vs effective rates.',
			'Avoidance is smart and legal; evasion is a crime.',
		],
	},
	'anti-scam': {
		id: 'anti-scam',
		label: 'Anti-scam',
		bucket: 'core-money-skills',
		color: '#6fae2e',
		philosophyLine:
			'If it’s urgent, guaranteed, or too good to be true, it’s a scam.',
		coreBeliefs: [
			'Real opportunities don’t pressure you to act right now.',
			'Never share codes, seed phrases, or remote access.',
			'Verify through official channels, not the number they gave you.',
		],
	},
	'risk-management': {
		id: 'risk-management',
		label: 'Risk management',
		bucket: 'core-money-skills',
		color: '#3f7d33',
		philosophyLine:
			'Protect against the losses that would wipe you out before chasing gains.',
		coreBeliefs: [
			'Insure the catastrophic, self-insure the small stuff.',
			'An emergency fund is the first line of defense.',
			'Diversify so no single event ends the game.',
		],
	},
	'adulting-life-skills': {
		id: 'adulting-life-skills',
		label: 'Adulting life skills',
		bucket: 'core-money-skills',
		color: '#7cb342',
		philosophyLine:
			'The boring admin nobody taught you is most of staying afloat.',
		coreBeliefs: [
			'Systems beat willpower — automate bills and savings.',
			'Read the contract before you sign it.',
			'Small recurring habits compound into stability.',
		],
	},
	'fintech-automation': {
		id: 'fintech-automation',
		label: 'Fintech & automation',
		bucket: 'core-money-skills',
		color: '#8bc34a',
		philosophyLine:
			'Set your money to move on its own so good behavior is the default.',
		coreBeliefs: [
			'Automate transfers on payday, before you can spend it.',
			'Use tools to track, but own the decisions yourself.',
			'Convenience is great until fees and dark patterns cost you.',
		],
	},

	// ── 2 · Money lenses (purples) ──────────────────────────────────────
	'behavioral-finance': {
		id: 'behavioral-finance',
		label: 'Behavioral finance',
		bucket: 'money-lenses',
		color: '#7b5ea7',
		thinker: 'Daniel Kahneman & Richard Thaler',
		philosophyLine:
			'Your brain’s shortcuts cost you money — design around them.',
		coreBeliefs: [
			'We feel losses about twice as hard as equivalent gains.',
			'Defaults and framing drive most financial choices.',
			'The biggest investing risk is usually the investor.',
		],
	},
	'money-psychology-housel': {
		id: 'money-psychology-housel',
		label: 'Money psychology (Housel)',
		bucket: 'money-lenses',
		color: '#8e6fc4',
		thinker: 'Morgan Housel',
		philosophyLine:
			'Doing well with money is about behavior, not intelligence.',
		coreBeliefs: [
			'Enough is a skill; goalposts that always move can’t be reached.',
			'Reasonable beats mathematically optimal if you can stick with it.',
			'Time and patience are the real engines of wealth.',
		],
	},
	'efficient-market': {
		id: 'efficient-market',
		label: 'Efficient market',
		bucket: 'money-lenses',
		color: '#6a4fa3',
		thinker: 'Eugene Fama',
		philosophyLine:
			'Prices already reflect what’s known, so beating the market is luck more than skill.',
		coreBeliefs: [
			'Public information is priced in almost instantly.',
			'Most active managers underperform a cheap index after fees.',
			'Don’t pay up to chase an edge that probably isn’t there.',
		],
	},
	'real-return': {
		id: 'real-return',
		label: 'Real vs nominal return',
		bucket: 'money-lenses',
		color: '#9575cd',
		philosophyLine:
			'Only what’s left after inflation actually grew your wealth.',
		coreBeliefs: [
			'A 5% return with 3% inflation is really 2%.',
			'Cash feels safe but quietly loses purchasing power.',
			'Judge every return in inflation-adjusted terms.',
		],
	},
	'debt-free-ramsey': {
		id: 'debt-free-ramsey',
		label: 'Debt-free (Ramsey)',
		bucket: 'money-lenses',
		color: '#5e4b8b',
		thinker: 'Dave Ramsey',
		philosophyLine:
			'Debt is the enemy; clear it with intensity, then never go back.',
		marketingHook: 'Debt is a behavior problem first.',
		coreBeliefs: [
			'Attack debts smallest-first for psychological wins (the snowball).',
			'Build a starter emergency fund, then kill all consumer debt.',
			'Behavior and discipline matter more than interest-rate math.',
		],
	},

	// ── 3 · Investing strategies (blues) & speculation (warm) ───────────
	'boglehead-indexing': {
		id: 'boglehead-indexing',
		label: 'Boglehead indexing',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#2f6fb0',
		thinker: 'John C. Bogle',
		philosophyLine:
			'Buy the whole market cheaply, hold forever, ignore the noise.',
		marketingHook: 'Most people lose by trying to be clever.',
		coreBeliefs: [
			'Low costs are the one thing you can control.',
			'Don’t look for the needle — buy the haystack.',
			'Stay the course; time in the market beats timing it.',
		],
	},
	'value-investing': {
		id: 'value-investing',
		label: 'Value investing',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#1f5f9e',
		thinker: 'Benjamin Graham',
		philosophyLine: 'Buy a dollar of value for fifty cents and wait.',
		marketingHook: 'Buy a dollar for fifty cents.',
		coreBeliefs: [
			'Price is what you pay; value is what you get.',
			'Demand a margin of safety on every purchase.',
			'Mr. Market’s moods are opportunities, not instructions.',
		],
	},
	'factor-investing': {
		id: 'factor-investing',
		label: 'Factor investing',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#3a82c4',
		thinker: 'Eugene Fama & Kenneth French',
		philosophyLine:
			'Tilt toward the traits that have historically paid a premium.',
		coreBeliefs: [
			'Size, value, momentum, and quality have explained excess returns.',
			'Factors can underperform for years — patience required.',
			'It’s rules-based, not stock-picking.',
		],
	},
	'dividend-investing': {
		id: 'dividend-investing',
		label: 'Dividend investing',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#4a90d9',
		philosophyLine:
			'Own companies that pay you to wait, and grow what they pay.',
		coreBeliefs: [
			'Prefer durable, growing dividends over high-but-shaky yields.',
			'Reinvested dividends are a huge share of long-run returns.',
			'A dividend is real cash; earnings can be massaged.',
		],
	},
	'growth-investing': {
		id: 'growth-investing',
		label: 'Growth investing',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#2d7dd2',
		thinker: 'Philip Fisher',
		philosophyLine:
			'Pay up for exceptional businesses compounding far into the future.',
		coreBeliefs: [
			'A great company can grow into a high price tag.',
			'Focus on durable advantages and large markets.',
			'Concentration in winners beats over-diversifying.',
		],
	},
	'buffett-munger': {
		id: 'buffett-munger',
		label: 'Buffett / Munger',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#1b4f8a',
		thinker: 'Warren Buffett & Charlie Munger',
		philosophyLine:
			'Wonderful businesses at fair prices, held for the very long run.',
		coreBeliefs: [
			'Buy quality with a durable moat, not cheap junk.',
			'Stay within your circle of competence.',
			'Be greedy when others are fearful.',
		],
	},
	'cashflow-assets': {
		id: 'cashflow-assets',
		label: 'Cashflow & assets',
		bucket: 'investing-strategies-speculation',
		group: 'strategy',
		color: '#5499e0',
		thinker: 'Robert Kiyosaki',
		philosophyLine:
			'Buy assets that put money in your pocket; avoid liabilities that take it out.',
		coreBeliefs: [
			'The rich buy assets; the rest buy liabilities they think are assets.',
			'Make money work for you instead of working for money.',
			'Financial education beats a high salary.',
		],
	},
	'technical-trading': {
		id: 'technical-trading',
		label: 'Technical trading',
		bucket: 'investing-strategies-speculation',
		group: 'speculation',
		color: '#e09b1f',
		philosophyLine:
			'Read price and volume patterns to time entries and exits.',
		coreBeliefs: [
			'Price action reflects all known information and crowd psychology.',
			'Trends persist until they don’t — manage risk per trade.',
			'High-effort, high-risk, and most retail traders lose money.',
		],
	},
	'options-derivatives': {
		id: 'options-derivatives',
		label: 'Options & derivatives',
		bucket: 'investing-strategies-speculation',
		group: 'speculation',
		color: '#d97a1f',
		philosophyLine:
			'Contracts that leverage, hedge, or bet on price — powerful and dangerous.',
		coreBeliefs: [
			'Options can hedge risk or amplify it to ruin.',
			'You can lose more than you put in with some positions.',
			'Most casual options buyers lose to time decay.',
		],
	},
	'crypto-web3': {
		id: 'crypto-web3',
		label: 'Crypto & web3',
		bucket: 'investing-strategies-speculation',
		group: 'speculation',
		color: '#d65a3a',
		thinker: 'Satoshi Nakamoto',
		philosophyLine:
			'Programmable, decentralized money — high conviction, high volatility.',
		marketingHook: 'Money without permission.',
		coreBeliefs: [
			'Self-custody means self-responsibility; not your keys, not your coins.',
			'Extreme volatility — only risk what you can lose.',
			'Most tokens go to zero; the space is full of scams.',
		],
	},

	// ── 4 · Freedom & lifestyle paths (teals) ───────────────────────────
	'fire-movement': {
		id: 'fire-movement',
		label: 'FIRE movement',
		bucket: 'freedom-lifestyle-paths',
		color: '#1f9e8f',
		thinker: 'Vicki Robin & Mr. Money Mustache',
		philosophyLine:
			'A high savings rate buys back your time decades early.',
		marketingHook: 'Money is stored time.',
		coreBeliefs: [
			'Your savings rate, not your income, sets your timeline.',
			'Money is life energy — spend it on what matters.',
			'Financial independence is optionality, not just early retirement.',
		],
	},
	'fi-math': {
		id: 'fi-math',
		label: 'FI math',
		bucket: 'freedom-lifestyle-paths',
		color: '#17897a',
		thinker: 'William Bengen (the 4% rule)',
		philosophyLine:
			'Roughly 25× your annual spending is the number that frees you.',
		marketingHook: 'Hit your number, buy your freedom.',
		coreBeliefs: [
			'A ~4% withdrawal rate has historically lasted 30+ years.',
			'Lower spending cuts the target on both ends.',
			'It’s arithmetic, not magic — know your real expenses.',
		],
	},
	'coast-fi-slow-fi': {
		id: 'coast-fi-slow-fi',
		label: 'Coast / slow FI',
		bucket: 'freedom-lifestyle-paths',
		color: '#2bb3a3',
		philosophyLine:
			'Front-load investing, then coast while compounding finishes the job.',
		coreBeliefs: [
			'Once invested enough early, you can stop adding and still hit FI.',
			'Trade max speed for a saner life now.',
			'Part-time or lower-stress work can cover only current costs.',
		],
	},
	'first-100k': {
		id: 'first-100k',
		label: 'First $100k',
		bucket: 'freedom-lifestyle-paths',
		color: '#0f7a6b',
		thinker: 'Charlie Munger',
		philosophyLine:
			'The first $100k is the hardest — then compounding does the heavy lifting.',
		coreBeliefs: [
			'Early on, savings rate matters far more than returns.',
			'Get the snowball big enough that growth outpaces contributions.',
			'Grind through the unglamorous early stage.',
		],
	},
	minimalism: {
		id: 'minimalism',
		label: 'Minimalism',
		bucket: 'freedom-lifestyle-paths',
		color: '#3ec9b8',
		thinker: 'The Minimalists',
		philosophyLine:
			'Own less so your money and attention go to what you value.',
		marketingHook: 'Own less, want less, owe less.',
		coreBeliefs: [
			'Stuff has an ongoing cost in money, space, and attention.',
			'Spend deliberately on the few things that add real value.',
			'Less clutter, more freedom.',
		],
	},
	frugality: {
		id: 'frugality',
		label: 'Frugality',
		bucket: 'freedom-lifestyle-paths',
		color: '#2a9d8f',
		philosophyLine:
			'Wring maximum value from every dollar without feeling deprived.',
		coreBeliefs: [
			'Cut hard on what you don’t care about to fund what you do.',
			'A dollar saved is worth more than a dollar earned (no tax).',
			'Frugal isn’t cheap — it’s intentional.',
		],
	},
	'rich-life-ramit': {
		id: 'rich-life-ramit',
		label: 'Rich life (Ramit)',
		bucket: 'freedom-lifestyle-paths',
		color: '#1abc9c',
		thinker: 'Ramit Sethi',
		philosophyLine:
			'Spend extravagantly on what you love, cut ruthlessly on what you don’t.',
		marketingHook: 'Spend hard on what matters. Cut the rest.',
		coreBeliefs: [
			'Automate the boring stuff so you can enjoy guilt-free spending.',
			'Define your own “rich life” — it isn’t a spreadsheet.',
			'Focus on big wins, not $3 lattes.',
		],
	},
	'life-design': {
		id: 'life-design',
		label: 'Life design',
		bucket: 'freedom-lifestyle-paths',
		color: '#14b8a6',
		thinker: 'Bill Burnett & Dave Evans',
		philosophyLine:
			'Treat your life and finances as a prototype you keep iterating.',
		coreBeliefs: [
			'Money is a means to a life you design, not the goal.',
			'Run small experiments before big commitments.',
			'Align spending and work with the life you actually want.',
		],
	},

	// ── 5 · Income & life domains (oranges) ─────────────────────────────
	'income-expansion': {
		id: 'income-expansion',
		label: 'Income expansion',
		bucket: 'income-life-domains',
		color: '#e0712f',
		philosophyLine:
			'There’s a floor on cutting costs but no ceiling on earning more.',
		coreBeliefs: [
			'Raises, switches, and side income beat extreme penny-pinching.',
			'Invest in skills that compound your earning power.',
			'Negotiate — it’s often the highest-paid hour of your year.',
		],
	},
	'career-capital': {
		id: 'career-capital',
		label: 'Career capital',
		bucket: 'income-life-domains',
		color: '#d8612b',
		thinker: 'Cal Newport',
		philosophyLine:
			'Build rare, valuable skills, then trade them for autonomy and pay.',
		coreBeliefs: [
			'“Follow your passion” is weak advice; get so good they can’t ignore you.',
			'Skills are leverage you can cash in later.',
			'Deep work builds the capital shallow busywork can’t.',
		],
	},
	'human-capital': {
		id: 'human-capital',
		label: 'Human capital',
		bucket: 'income-life-domains',
		color: '#ec8b3a',
		thinker: 'Gary Becker',
		philosophyLine:
			'You are your own biggest asset — invest in yourself accordingly.',
		coreBeliefs: [
			'Education and health are investments with real returns.',
			'Young people are rich in human capital, poor in financial capital.',
			'Spending that raises future earnings is investing.',
		],
	},
	'real-estate-housing': {
		id: 'real-estate-housing',
		label: 'Real estate & housing',
		bucket: 'income-life-domains',
		color: '#c9551f',
		philosophyLine:
			'Housing is your biggest expense and often your biggest decision.',
		coreBeliefs: [
			'Rent vs buy is math plus lifestyle, not a moral question.',
			'A home you live in is part lifestyle, part investment.',
			'Leverage cuts both ways — respect the downside.',
		],
	},
	'relationship-family-money': {
		id: 'relationship-family-money',
		label: 'Relationships & family money',
		bucket: 'income-life-domains',
		color: '#e89a4d',
		philosophyLine:
			'Money is one of the top sources of conflict — talk about it on purpose.',
		coreBeliefs: [
			'Align on goals and values, not just budgets.',
			'Transparency prevents most money fights.',
			'Combining finances is a system choice, not a loyalty test.',
		],
	},

	// ── 6 · Macro & wealth perspectives (slate) ─────────────────────────
	'macro-money': {
		id: 'macro-money',
		label: 'Macro money',
		bucket: 'macro-wealth-perspectives',
		color: '#3a5a8c',
		thinker: 'Ray Dalio',
		philosophyLine:
			'Understand the machine — credit, rates, and cycles move everything.',
		coreBeliefs: [
			'The economy runs on credit cycles, short and long.',
			'Central banks and rates set the weather for all assets.',
			'Zoom out: most “new” events have historical templates.',
		],
	},
	'hard-money': {
		id: 'hard-money',
		label: 'Hard money',
		bucket: 'macro-wealth-perspectives',
		color: '#2c4a73',
		thinker: 'The Austrian school (Mises, Hayek)',
		philosophyLine:
			'Sound money is scarce money; printing it steals value over time.',
		coreBeliefs: [
			'Inflation is a hidden tax on savers.',
			'Scarce assets (gold, bitcoin) hedge currency debasement.',
			'Distrust unlimited money creation.',
		],
	},
	'keynesian-mmt': {
		id: 'keynesian-mmt',
		label: 'Keynesian / MMT',
		bucket: 'macro-wealth-perspectives',
		color: '#47668f',
		thinker: 'Keynes & Stephanie Kelton',
		philosophyLine:
			'Governments can spend to steer the economy; the real limit is inflation.',
		coreBeliefs: [
			'In downturns, public spending fills the demand gap.',
			'A currency-issuer doesn’t fund like a household.',
			'Inflation, not solvency, is the binding constraint.',
		],
	},
	'inequality-awareness': {
		id: 'inequality-awareness',
		label: 'Inequality awareness',
		bucket: 'macro-wealth-perspectives',
		color: '#54759e',
		thinker: 'Thomas Piketty',
		philosophyLine:
			'When returns on capital outpace wages, wealth concentrates.',
		coreBeliefs: [
			'Owning assets, not just earning wages, builds wealth.',
			'Structural forces shape outcomes, not just effort.',
			'Understand the system you’re playing in.',
		],
	},
	'anti-fragility': {
		id: 'anti-fragility',
		label: 'Anti-fragility',
		bucket: 'macro-wealth-perspectives',
		color: '#1f3a5c',
		thinker: 'Nassim Nicholas Taleb',
		philosophyLine:
			'Build a financial life that gains from disorder instead of breaking.',
		coreBeliefs: [
			'Avoid ruin first; survival enables everything else.',
			'Keep optionality and a barbell of safe + risky.',
			'Rare, extreme events drive most outcomes — plan for them.',
		],
	},
};

// ── Derived lists & lookups ──────────────────────────────────────────────
export const LISTED_SCHOOLS: ListedSchool[] = Object.keys(
	SCHOOL_META,
) as ListedSchool[];

export const BUCKETS_IN_ORDER: BucketMeta[] = SCHOOL_BUCKETS.map(
	(b) => BUCKET_META[b],
).sort((a, b) => a.order - b.order);

export function getSchoolMeta(id: string): SchoolMetaEntry | undefined {
	return (SCHOOL_META as Record<string, SchoolMetaEntry>)[id];
}

export function getSchoolsByBucket(bucket: SchoolBucket): SchoolMetaEntry[] {
	return LISTED_SCHOOLS.map((id) => SCHOOL_META[id]).filter(
		(s) => s.bucket === bucket,
	);
}

export function getGroupedSchools(bucket: SchoolBucket): {
	strategy: SchoolMetaEntry[];
	speculation: SchoolMetaEntry[];
	ungrouped: SchoolMetaEntry[];
} {
	const all = getSchoolsByBucket(bucket);
	return {
		strategy: all.filter((s) => s.group === 'strategy'),
		speculation: all.filter((s) => s.group === 'speculation'),
		ungrouped: all.filter((s) => !s.group),
	};
}

// ── Featured worldviews (Learn hub) ──────────────────────────────────────
/** Curated shortlist of marquee schools used by the Learn hub hero card and
 *  the worldview carousel. Every id here MUST carry a `marketingHook`. Order
 *  is the carousel order; the hero rotates through this same pool. */
export const FEATURED_SCHOOL_IDS: ListedSchool[] = [
	'fire-movement',
	'boglehead-indexing',
	'rich-life-ramit',
	'debt-free-ramsey',
	'value-investing',
	'crypto-web3',
	'minimalism',
	'fi-math',
];

/** Resolves FEATURED_SCHOOL_IDS to entries, skipping any that lost their
 *  marketingHook (defensive — keeps the hub from rendering a blank slogan). */
export function getFeaturedSchools(): SchoolMetaEntry[] {
	return FEATURED_SCHOOL_IDS.map((id) => SCHOOL_META[id]).filter(
		(s): s is SchoolMetaEntry => Boolean(s && s.marketingHook),
	);
}
